import { type RefObject, useCallback, useEffect, useLayoutEffect } from 'react'
import type { UseFormWatch } from 'react-hook-form'

const fitToContent = (textarea: HTMLTextAreaElement) => {
  textarea.style.height = 'auto'
  if (textarea.scrollHeight === 0) {
    return
  }

  const style = window.getComputedStyle(textarea)
  const borders =
    Number.parseFloat(style.borderTopWidth) +
    Number.parseFloat(style.borderBottomWidth)
  const paddings =
    Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom)
  const height =
    style.boxSizing === 'border-box'
      ? textarea.scrollHeight + borders
      : textarea.scrollHeight - paddings
  textarea.style.height = `${height}px`
}

export const useTextareaAutoResize = ({
  ref,
  enabled,
  name,
  watch
}: {
  ref: RefObject<HTMLTextAreaElement>
  enabled: boolean
  name?: string
  watch?: UseFormWatch<any>
}) => {
  const resize = useCallback(() => {
    if (enabled && ref.current) {
      fitToContent(ref.current)
    }
  }, [enabled, ref])

  useLayoutEffect(() => {
    resize()
  })

  // Values set by the form itself (setValue, reset) fire no input event
  useEffect(() => {
    if (!enabled || !watch || !name) {
      return
    }
    const subscription = watch((_values, { name: changedName }) => {
      if (changedName === undefined || changedName === name) {
        window.requestAnimationFrame(resize)
      }
    })
    return () => subscription.unsubscribe()
  }, [enabled, watch, name, resize])

  // The text wraps differently when the width changes
  useEffect(() => {
    const textarea = ref.current
    if (!enabled || !textarea) {
      return
    }
    let width = textarea.offsetWidth
    const observer = new ResizeObserver(() => {
      if (textarea.offsetWidth !== width) {
        width = textarea.offsetWidth
        resize()
      }
    })
    observer.observe(textarea)
    return () => observer.disconnect()
  }, [enabled, ref, resize])

  return resize
}
